import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import {
  ShieldAlert,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Ban,
  Mail,
  Phone,
  IdCard,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";

function formatDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function fullName(u: { firstName?: string | null; lastName?: string | null; email?: string | null }): string {
  const n = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
  return n || u.email || "—";
}

type Section = "siret" | "email" | "phone";

export default function OpsDuplicates() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);

  const utils = trpc.useUtils();
  const { data, isLoading, error } = trpc.admin.opsAntiFraud.findDuplicateAccounts.useQuery();

  const [openSections, setOpenSections] = useState<Record<Section, boolean>>({
    siret: true,
    email: false,
    phone: false,
  });
  const [suspendTarget, setSuspendTarget] = useState<{ artisanProfileId: string; label: string } | null>(null);
  const [suspendReason, setSuspendReason] = useState("");

  const suspendMutation = trpc.admin.travauxbtp.suspendArtisan.useMutation({
    onSuccess: () => {
      toast.success("Artisan suspendu");
      setSuspendTarget(null);
      setSuspendReason("");
      utils.admin.opsAntiFraud.findDuplicateAccounts.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erreur lors de la suspension"),
  });

  const toggleSection = (s: Section) => setOpenSections((prev) => ({ ...prev, [s]: !prev[s] }));

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="space-y-4 p-6">
          <div className="h-8 w-64 bg-muted rounded animate-pulse" />
          <div className="h-32 bg-muted rounded animate-pulse" />
          <div className="h-32 bg-muted rounded animate-pulse" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="p-6">
          <Card>
            <CardContent className="py-8 text-center">
              <ShieldAlert className="h-8 w-8 mx-auto mb-2 text-destructive" />
              <p className="text-sm text-muted-foreground">{error.message}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => utils.admin.opsAntiFraud.findDuplicateAccounts.invalidate()}
              >
                Reessayer
              </Button>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  const totals = data?.totals ?? { siret: 0, email: 0, phone: 0 };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Detection multi-comptes</h1>
            <p className="text-muted-foreground text-sm">
              Diagnostic anti-fraude — SIRET, email et telephone partages entre plusieurs comptes.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => utils.admin.opsAntiFraud.findDuplicateAccounts.invalidate()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Rafraichir
          </Button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard icon={<IdCard className="h-5 w-5" />} label="SIRET dupliques" count={totals.siret} tone="destructive" />
          <KpiCard icon={<Mail className="h-5 w-5" />} label="Emails dupliques (soft-deleted)" count={totals.email} tone="warning" />
          <KpiCard icon={<Phone className="h-5 w-5" />} label="Telephones dupliques" count={totals.phone} tone="warning" />
        </div>

        {/* Section: SIRET */}
        <SectionCard
          title="SIRET partages entre plusieurs profils artisans"
          count={data?.bySiret.length ?? 0}
          open={openSections.siret}
          onToggle={() => toggleSection("siret")}
        >
          {(data?.bySiret.length ?? 0) === 0 ? (
            <EmptyRow>Aucun SIRET dupliqué.</EmptyRow>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SIRET</TableHead>
                  <TableHead>Profil artisan</TableHead>
                  <TableHead>Email user</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Cree le</TableHead>
                  {canAct && <TableHead className="text-right">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.bySiret.flatMap((group) =>
                  group.profiles.map((p, i) => (
                    <TableRow key={p.id} className={i === 0 ? "border-t-2" : ""}>
                      <TableCell className="font-mono text-xs">
                        {i === 0 ? (
                          <Badge variant="destructive" className="font-mono">{group.siret}</Badge>
                        ) : (
                          <span className="text-muted-foreground">↳</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{p.companyName}</span>
                          <span className="block text-xs text-muted-foreground">
                            {fullName(p.User)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{p.User.email}</TableCell>
                      <TableCell>
                        <VerifBadge status={p.verificationStatus} pending={p.pendingVerification} />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(p.createdAt)}</TableCell>
                      {canAct && (
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7"
                            onClick={() =>
                              setSuspendTarget({
                                artisanProfileId: p.id,
                                label: `${p.companyName} (${p.User.email})`,
                              })
                            }
                          >
                            <Ban className="h-3.5 w-3.5 mr-1" />
                            Suspendre
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  )),
                )}
              </TableBody>
            </Table>
          )}
        </SectionCard>

        {/* Section: Emails (soft-deleted duplicates) */}
        <SectionCard
          title="Emails partages (uniquement comptes soft-deleted)"
          count={data?.byEmail.length ?? 0}
          open={openSections.email}
          onToggle={() => toggleSection("email")}
        >
          {(data?.byEmail.length ?? 0) === 0 ? (
            <EmptyRow>Aucun email dupliqué (l'unicite est garantie cote DB pour les comptes actifs).</EmptyRow>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Profil artisan</TableHead>
                  <TableHead>Cree le</TableHead>
                  <TableHead>Supprime le</TableHead>
                  {canAct && <TableHead className="text-right">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.byEmail.flatMap((group) =>
                  group.users.map((u, i) => (
                    <TableRow key={u.id} className={i === 0 ? "border-t-2" : ""}>
                      <TableCell className="text-xs">
                        {i === 0 ? <Badge variant="destructive">{group.email}</Badge> : <span className="text-muted-foreground">↳</span>}
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{fullName(u)}</span>
                          {u.companyId && (
                            <span className="block text-xs text-muted-foreground">company {u.companyId.slice(0, 8)}…</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {u.ArtisanProfile ? (
                          <span className="text-sm">{u.ArtisanProfile.companyName}</span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(u.createdAt)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(u.deletedAt)}</TableCell>
                      {canAct && (
                        <TableCell className="text-right">
                          {u.ArtisanProfile ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7"
                              onClick={() =>
                                setSuspendTarget({
                                  artisanProfileId: u.ArtisanProfile!.id,
                                  label: `${u.ArtisanProfile!.companyName} (${u.email})`,
                                })
                              }
                            >
                              <Ban className="h-3.5 w-3.5 mr-1" />
                              Suspendre
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )),
                )}
              </TableBody>
            </Table>
          )}
        </SectionCard>

        {/* Section: Phone */}
        <SectionCard
          title="Telephones partages entre plusieurs users"
          count={data?.byPhone.length ?? 0}
          open={openSections.phone}
          onToggle={() => toggleSection("phone")}
        >
          {(data?.byPhone.length ?? 0) === 0 ? (
            <EmptyRow>Aucun telephone dupliqué.</EmptyRow>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Telephone</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Profil artisan</TableHead>
                  <TableHead>Cree le</TableHead>
                  {canAct && <TableHead className="text-right">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.byPhone.flatMap((group) =>
                  group.users.map((u, i) => (
                    <TableRow key={u.id} className={i === 0 ? "border-t-2" : ""}>
                      <TableCell className="font-mono text-xs">
                        {i === 0 ? <Badge variant="destructive">{group.phone}</Badge> : <span className="text-muted-foreground">↳</span>}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{fullName(u)}</span>
                        {u.deletedAt && (
                          <Badge variant="outline" className="ml-2 text-xs">supprime</Badge>
                        )}
                        {u.isBlocked && (
                          <Badge variant="outline" className="ml-2 text-xs border-destructive text-destructive">bloque</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{u.email}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {u.companyId ? `${u.companyId.slice(0, 8)}…` : "—"}
                      </TableCell>
                      <TableCell>
                        {u.ArtisanProfile ? (
                          <span className="text-sm">{u.ArtisanProfile.companyName}</span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(u.createdAt)}</TableCell>
                      {canAct && (
                        <TableCell className="text-right">
                          {u.ArtisanProfile ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7"
                              onClick={() =>
                                setSuspendTarget({
                                  artisanProfileId: u.ArtisanProfile!.id,
                                  label: `${u.ArtisanProfile!.companyName} (${u.email})`,
                                })
                              }
                            >
                              <Ban className="h-3.5 w-3.5 mr-1" />
                              Suspendre
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )),
                )}
              </TableBody>
            </Table>
          )}
        </SectionCard>
      </div>

      {/* Suspend dialog */}
      <Dialog open={!!suspendTarget} onOpenChange={(o) => { if (!o) { setSuspendTarget(null); setSuspendReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspendre cet artisan ?</DialogTitle>
            <DialogDescription>
              {suspendTarget?.label}
              <br />
              L'artisan sera marque REJECTED et passe en NOT_TAKING_NEW_PROJECTS.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Raison interne (optionnel)"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendTarget(null)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!suspendTarget) return;
                suspendMutation.mutate({
                  artisanProfileId: suspendTarget.artisanProfileId,
                  reason: suspendReason.trim() || undefined,
                });
              }}
              disabled={suspendMutation.isPending}
            >
              {suspendMutation.isPending ? "Suspension..." : "Confirmer la suspension"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function KpiCard({
  icon,
  label,
  count,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  tone: "destructive" | "warning";
}) {
  const cls =
    tone === "destructive"
      ? "border-destructive/50 bg-destructive/5 text-destructive"
      : "border-orange-500/50 bg-orange-500/5 text-orange-600";
  return (
    <Card className={count > 0 ? cls.split(" ").slice(0, 2).join(" ") : ""}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${count > 0 ? cls.split(" ").slice(1, 2).join(" ") : "bg-muted"}`}>
            {icon}
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={`text-2xl font-bold ${count > 0 ? cls.split(" ").slice(2).join(" ") : ""}`}>{count}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionCard({
  title,
  count,
  open,
  onToggle,
  children,
}: {
  title: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="font-medium">{title}</span>
          <Badge variant="secondary" className="ml-2">{count}</Badge>
        </div>
      </button>
      {open && (
        <CardContent className="p-0 border-t">
          {children}
        </CardContent>
      )}
    </Card>
  );
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 py-6 text-sm text-muted-foreground text-center">{children}</div>
  );
}

function VerifBadge({ status, pending }: { status: string; pending: boolean }) {
  if (pending) return <Badge variant="outline" className="border-yellow-500 text-yellow-600">PENDING</Badge>;
  switch (status) {
    case "VERIFIED":
      return <Badge variant="outline" className="border-emerald-500 text-emerald-600">VERIFIED</Badge>;
    case "REJECTED":
      return <Badge variant="outline" className="border-destructive text-destructive">REJECTED</Badge>;
    case "PENDING":
      return <Badge variant="outline" className="border-yellow-500 text-yellow-600">PENDING</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}
